/**
 * Artifact Types
 *
 * TypeScript types for Leo Artifacts - structured XML references to internal resources.
 *
 * Artifacts are the canonical way to reference campaigns, ad sets, ads, assets, and reports.
 * Every artifact must be grounded - Leo cannot fabricate IDs. Each artifact must reference
 * data from a tool call.
 */

import { Platform } from '@enums/Platform';


export type AdsEntityType = 'campaign' | 'adset' | 'ad';
export type ReportsEntityType = 'report';
export type AssetsEntityType = 'asset';
export type FilesEntityType = 'file';
export type ScheduledWorkEntityType = 'scheduledWork';
export type BatchPlanEntityType = 'batch_plan';
export type ObjectiveEntityType = 'objective';
/**
 * Valid artifact types - matches backend ArtifactType enum
 */
export type ArtifactType = AdsEntityType | ReportsEntityType | AssetsEntityType | FilesEntityType | ScheduledWorkEntityType | BatchPlanEntityType | ObjectiveEntityType;

/**
 * Represents a grounded reference to an internal resource.
 *
 * Artifacts are structured references that Leo uses to identify campaigns,
 * ad sets, ads, assets, and reports. Every artifact must be grounded in
 * a tool call - Leo cannot fabricate IDs.
 */
export type NonBatchPlanArtifactType = Exclude<ArtifactType, 'batch_plan'>;

export interface BaseArtifact {
  type: NonBatchPlanArtifactType;
  /** Canonical identifier (invariant, from tool output) */
  id: string;
  /** Display name (snapshot, may be stale) */
  name: string;
  /** Platform identifier (e.g., 'meta') - required for campaign/adset/ad */
  platform?: string;
  /** The tool that provided this ID (e.g., 'get_campaigns', 'create_ad', 'user_input') */
  groundedBy: string;
  /** Thumbnail URL for asset display */
  thumbnail?: string;
  /** Asset type (image, video) for proper thumbnail handling */
  assetType?: string;
}

export interface BatchPlanArtifact {
  type: 'batch_plan';
  id: string;
  name: string;
  platform?: string;
  groundedBy?: string;
  /** Number of batches (batch_plan only) */
  batches?: string;
  /** Total number of ads (batch_plan only) */
  totalAds?: string;
}

export type Artifact = BaseArtifact | BatchPlanArtifact;


/**
 * Parse a single artifact XML string into an Artifact object.
 *
 * @param xmlString - The artifact XML string to parse
 * @returns The parsed Artifact, or null if parsing fails
 *
 * @example
 * parseArtifactXml('<artifact type="campaign" id="123" platform="meta" name="Summer Sale" grounded_by="get_campaigns"/>')
 * // Returns: { type: 'campaign', id: '123', platform: 'meta', name: 'Summer Sale', groundedBy: 'get_campaigns' }
 */
export function parseArtifactXml(xmlString: string): Artifact | null {
  // Extract attributes string
  const match = /<artifact\s+([^>]*?)\/>/i.exec(xmlString);
  if (!match) return null;

  const attrsString = match[1];
  const attrs: Record<string, string> = {};

  // Parse all attributes
  let attrMatch: RegExpExecArray | null;
  const attrRegex = /(\w+)="([^"]*)"/g;
  while ((attrMatch = attrRegex.exec(attrsString)) !== null) {
    attrs[attrMatch[1]] = attrMatch[2];
  }

  // Validate required fields
  const type = attrs.type as ArtifactType | undefined;
  const id = attrs.id;
  const name = attrs.name;
  const groundedBy = attrs.grounded_by;

  // batch_plan artifacts can omit grounded_by; they use 'batches' and 'total_ads'
  if (type === 'batch_plan') {
    if (!id || !name) {
      console.warn('Invalid batch_plan artifact XML - missing id or name:', xmlString);
      return null;
    }
  } else if (!type || !id || !name || !groundedBy) {
    console.warn('Invalid artifact XML - missing required attributes:', xmlString);
    return null;
  }

  // Validate type is one of the allowed values
  const validTypes: ArtifactType[] = ['campaign', 'adset', 'ad', 'asset', 'file', 'report', 'scheduledWork', 'batch_plan', 'objective'];
  if (!validTypes.includes(type)) {
    console.warn('Invalid artifact type:', type);
    return null;
  }

  // HTML unescape the name
  const unescapedName = name
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&amp;/g, '&')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'");

  // Parse thumbnail URL (unescape HTML entities)
  const thumbnail = attrs.thumbnail
    ? attrs.thumbnail
        .replace(/&quot;/g, '"')
        .replace(/&amp;/g, '&')
    : undefined;

  if (type === 'batch_plan') {
    return {
      type: 'batch_plan',
      id,
      name: unescapedName,
      platform: attrs.platform,
      groundedBy: groundedBy,
      batches: attrs.batches,
      totalAds: attrs.total_ads,
    };
  }

  return {
    type,
    id,
    name: unescapedName,
    platform: attrs.platform,
    groundedBy: groundedBy,
    // Display-only properties for assets
    thumbnail,
    assetType: attrs.asset_type,
  };
}

/**
 * Result of finding artifacts in content, including their positions.
 */
export interface ArtifactMatch {
  artifact: Artifact;
  startIndex: number;
  endIndex: number;
  rawXml: string;
}

/**
 * Find all artifact XML tags in content and parse them.
 *
 * @param content - The content string to search
 * @returns Array of parsed artifacts with their positions
 *
 * @example
 * findArtifacts('Here is <artifact type="campaign" id="123" platform="meta" name="Test" grounded_by="get_campaigns"/> for you')
 * // Returns: [{ artifact: {...}, startIndex: 8, endIndex: 95, rawXml: '<artifact .../>' }]
 */
export function findArtifacts(content: string): ArtifactMatch[] {
  const matches: ArtifactMatch[] = [];
  const regex = /<artifact\s+[^>]*?\/>/gi;
  let match: RegExpExecArray | null;

  while ((match = regex.exec(content)) !== null) {
    const artifact = parseArtifactXml(match[0]);
    if (artifact) {
      matches.push({
        artifact,
        startIndex: match.index,
        endIndex: match.index + match[0].length,
        rawXml: match[0],
      });
    }
  }

  return matches;
}

/**
 * Serialize an Artifact to XML string format.
 *
 * @param artifact - The artifact to serialize
 * @returns XML string representation
 *
 * @example
 * artifactToXml({ type: 'campaign', id: '123', platform: 'meta', name: 'Summer Sale', groundedBy: 'user_input' })
 * // Returns: '<artifact type="campaign" id="123" platform="meta" name="Summer Sale" grounded_by="user_input"/>'
 */
export function artifactToXml(artifact: Artifact): string {
  // HTML escape the name for safety
  const safeName = artifact.name
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

  const attrs: string[] = [
    `type="${artifact.type}"`,
    `id="${artifact.id}"`,
  ];

  if (artifact.platform) {
    attrs.push(`platform="${artifact.platform}"`);
  }

  attrs.push(`name="${safeName}"`);
  if (artifact.groundedBy) {
    attrs.push(`grounded_by="${artifact.groundedBy}"`);
  }

  if (artifact.type === 'batch_plan') {
    if (artifact.batches) {
      attrs.push(`batches="${artifact.batches}"`);
    }
    if (artifact.totalAds) {
      attrs.push(`total_ads="${artifact.totalAds}"`);
    }
  }

  // Include thumbnail and assetType for asset types (used for hover preview)
  if (artifact.type === 'asset') {
    if (artifact.thumbnail) {
      // URL-encode the thumbnail to safely include it in the attribute
      const safeThumbnail = artifact.thumbnail
        .replace(/&/g, '&amp;')
        .replace(/"/g, '&quot;');
      attrs.push(`thumbnail="${safeThumbnail}"`);
    }
    if (artifact.assetType) {
      attrs.push(`asset_type="${artifact.assetType}"`);
    }
  }

  return `<artifact ${attrs.join(' ')}/>`;
}

/**
 * Convert platform string to Platform enum.
 * Returns undefined if platform is not recognized.
 */
export function artifactPlatformToPlatformEnum(platform?: string): Platform | undefined {
  if (!platform) return undefined;

  const normalized = platform.toLowerCase();
  switch (normalized) {
    case 'meta':
    case 'facebook':
    case 'instagram':
      return Platform.META;
    case 'google':
      return Platform.GOOGLE;
    case 'linkedin':
      return Platform.LINKEDIN;
    case 'tiktok':
      return Platform.TIKTOK;
    default:
      return undefined;
  }
}

/**
 * Check if an artifact type requires a platform.
 */
export function artifactTypeRequiresPlatform(type: ArtifactType): boolean {
  return type === 'campaign' || type === 'adset' || type === 'ad';
}

const DOCUMENT_EXTENSIONS = ['pdf', 'docx', 'pptx', 'xlsx'];
const DOCUMENT_MIME_PREFIXES = ['application/pdf', 'application/vnd.openxmlformats'];

/**
 * Determine if an asset is a document (PDF, Word, Excel, PowerPoint).
 */
function isDocumentAsset(mimeType?: string, url?: string, name?: string): boolean {
  if (mimeType && DOCUMENT_MIME_PREFIXES.some(p => mimeType.startsWith(p))) {
    return true;
  }
  const source = url || name || '';
  const ext = source.split('?')[0].split('.').pop()?.toLowerCase();
  return DOCUMENT_EXTENSIONS.includes(ext || '');
}

/**
 * Determine if an asset is a video based on MIME type or URL extension.
 */
function isVideoAsset(mimeType?: string, url?: string): boolean {
  if (mimeType?.startsWith('video/')) {
    return true;
  }
  if (url) {
    const extension = url.split('.').pop()?.toLowerCase();
    return ['mp4', 'webm', 'ogg', 'mov', 'avi', 'wmv', 'flv', 'mkv'].includes(extension || '');
  }
  return false;
}

/**
 * Convert an Asset to an Artifact for use in chat.
 * The asset_id becomes the artifact id, enabling Leo to reference the asset.
 *
 * @param asset - The asset to convert (must have asset_id)
 * @returns An Artifact with type='asset' and display properties for thumbnail
 */
export function assetToArtifact(asset: {
  asset_id: string;
  asset_name?: string | null;
  asset_original_name?: string | null;
  asset_mime_type?: string | null;
  url?: string | null;
  s3_path_to_thumbnail?: string | null;
}): Artifact {
  const name = asset.asset_original_name || asset.asset_name || 'Asset';
  const mimeType = asset.asset_mime_type ?? undefined;
  const url = asset.url ?? undefined;

  // Route documents (PDF, Word, Excel, PowerPoint) to 'file' type
  if (isDocumentAsset(mimeType, url, name)) {
    return {
      type: 'file',
      id: asset.asset_id,
      name,
      groundedBy: 'user_input',
    };
  }

  return {
    type: 'asset',
    id: asset.asset_id,
    name,
    groundedBy: 'user_input',
    // Display-only properties
    thumbnail: asset.s3_path_to_thumbnail || asset.url || undefined,
    assetType: isVideoAsset(mimeType, url) ? 'video' : 'image',
  };
}
